/**
 * What each aircraft reports around it, drawn read-only on the Command map.
 *
 * AIS ships with a one-minute course line, ADS-B traffic with a short heading
 * line, the points ArduPilot's avoidance is steering round as red rings, and
 * proximity hits as arcs at their bearing and distance, red when close.
 * Clicking a ship, an aircraft or an avoidance point shows what was reported
 * and which of our aircraft reported it.
 *
 * **None of it can look like ours.** Our aircraft are DOM markers with a
 * heading arrow and a bare callsign in the friendly blue. Everything here is a
 * GeoJSON circle in a hue that is not the friendly one, and every label carries
 * what kind of report it is (AIS, ADS-B, AVOID), so a contact read aloud over a
 * radio still says it is somebody else.
 *
 * **GeoJSON layers, not markers.** A harbour reports a few hundred AIS ships,
 * and a DOM marker each would stall the map.
 *
 * **One contact, however many aircraft hear it.** Two boats in one harbour
 * both hear the same ship. Drawn twice, it reads as two ships; here it is drawn
 * once, from the freshest report, and the card lists every reporter.
 *
 * Colours are hex for the reason `useExternalTrackLayer.tsx` records: MapLibre
 * cannot parse oklch.
 */

import { X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { listSurroundings, type SurroundingsPicture } from '@/api/surroundings';
import { geoJsonSource, useMapController, type MapLike } from '@/map/MapCanvas';
import { useDroneStore } from '@/stores/droneStore';
import { useMapStore } from '@/stores/mapStore';

export const SURROUNDINGS_COLOR = {
  ship: '#2bb3c0',
  traffic: '#f0a53f',
  avoid: '#ed4042',
  near: '#ed4042',
  mid: '#f0a53f',
  far: '#e6c540',
} as const;

/** Proximity arcs closer than these are drawn red, then amber. */
export const PROXIMITY_NEAR_M = 20;
export const PROXIMITY_MID_M = 60;

/** How often the picture is re-read while the map is up. */
export const SURROUNDINGS_POLL_MS = 2000;

const SOURCES = {
  prox: 'surroundings-proximity',
  vectors: 'surroundings-vectors',
  ships: 'surroundings-ships',
  traffic: 'surroundings-traffic',
  avoid: 'surroundings-avoid',
} as const;

const PROX_LAYER = 'surroundings-proximity-line';
const VECTOR_LAYER = 'surroundings-vector-line';
export const SHIP_LAYER = 'surroundings-ship';
export const TRAFFIC_LAYER = 'surroundings-traffic';
export const AVOID_LAYER = 'surroundings-avoid';
const LABEL_LAYER = 'surroundings-label';
const CLICKABLE = [SHIP_LAYER, TRAFFIC_LAYER, AVOID_LAYER] as const;
const ALL_LAYERS = [PROX_LAYER, VECTOR_LAYER, AVOID_LAYER, SHIP_LAYER, TRAFFIC_LAYER, LABEL_LAYER];

type Feature = {
  type: 'Feature';
  properties: Record<string, unknown>;
  geometry: { type: 'Point'; coordinates: [number, number] } | { type: 'LineString'; coordinates: [number, number][] };
};

export interface SurroundingsFeatures {
  ships: Feature[];
  traffic: Feature[];
  avoid: Feature[];
  vectors: Feature[];
  prox: Feature[];
}

/** [lon, lat] a distance along a bearing. Flat-earth, which is fine at obstacle range. */
export function offsetLonLat(lat: number, lon: number, bearingDeg: number, metres: number): [number, number] {
  const b = (bearingDeg * Math.PI) / 180;
  const dLat = (metres * Math.cos(b)) / 111_320;
  const dLon = (metres * Math.sin(b)) / (111_320 * Math.cos((lat * Math.PI) / 180));
  return [lon + dLon, lat + dLat];
}

function finite(...values: unknown[]): boolean {
  return values.every((v) => typeof v === 'number' && Number.isFinite(v));
}

function age(seconds: number): string {
  return `${Math.max(0, Math.round(seconds))} s ago`;
}

interface Merged {
  kind: 'ship' | 'traffic' | 'avoid';
  lat: number;
  lon: number;
  ageS: number;
  title: string;
  label: string;
  lines: string[];
  reporters: string[];
  vector: [number, number][] | null;
}

/**
 * Everything to draw, from every visible aircraft's picture.
 *
 * Pure, so the merge and the geometry can be tested without a map.
 * `callsignFor` turns a reporting drone id into the name the operator knows.
 */
export function surroundingsFeatures(
  pictures: SurroundingsPicture[],
  callsignFor: (droneId: string) => string,
): SurroundingsFeatures {
  const merged = new Map<string, Merged>();
  const prox: Feature[] = [];

  const keep = (key: string, next: Merged): void => {
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, next);
      return;
    }
    const reporters = [...new Set([...existing.reporters, ...next.reporters])];
    merged.set(key, next.ageS < existing.ageS ? { ...next, reporters } : { ...existing, reporters });
  };

  for (const picture of pictures) {
    const reporter = callsignFor(picture.droneId);

    for (const ship of picture.ais ?? []) {
      if (!finite(ship.lat, ship.lon)) continue;
      const name = ship.name ?? ship.callsign ?? `MMSI ${ship.id}`;
      const lines = [`${ship.lat.toFixed(5)}, ${ship.lon.toFixed(5)}`, `MMSI ${ship.id}`];
      if (ship.callsign) lines.push(`CALLSIGN ${ship.callsign}`);
      if (ship.courseDeg !== null) lines.push(`COURSE ${Math.round(ship.courseDeg)}°`);
      if (ship.speedMs !== null) lines.push(`SPEED ${((ship.speedMs * 3600) / 1852).toFixed(1)} kn`);
      if (ship.lengthM !== null) lines.push(`LENGTH ${Math.round(ship.lengthM)} m`);
      const moving = ship.courseDeg !== null && ship.speedMs !== null && ship.speedMs > 0.3;
      keep(`ship:${ship.id}`, {
        kind: 'ship',
        lat: ship.lat,
        lon: ship.lon,
        ageS: ship.ageS,
        title: `AIS · ${name}`,
        label: `AIS ${name}`,
        lines,
        reporters: [reporter],
        vector: moving
          ? [
              [ship.lon, ship.lat],
              // Where it will be in a minute at this course and speed.
              offsetLonLat(ship.lat, ship.lon, ship.courseDeg as number, Math.min(1500, Math.max(40, (ship.speedMs as number) * 60))),
            ]
          : null,
      });
    }

    for (const contact of picture.traffic ?? []) {
      if (!finite(contact.lat, contact.lon)) continue;
      const name = contact.callsign ?? contact.icaoHex;
      const lines = [`${contact.lat.toFixed(5)}, ${contact.lon.toFixed(5)}`, `ICAO ${contact.icaoHex}`];
      if (contact.altM !== null) lines.push(`ALT ${Math.round(contact.altM)} m`);
      if (contact.headingDeg !== null) lines.push(`HEADING ${Math.round(contact.headingDeg)}°`);
      if (contact.speedMs !== null) lines.push(`SPEED ${contact.speedMs.toFixed(0)} m/s`);
      const moving = contact.headingDeg !== null && contact.speedMs !== null && contact.speedMs > 1;
      keep(`traffic:${contact.id}`, {
        kind: 'traffic',
        lat: contact.lat,
        lon: contact.lon,
        ageS: contact.ageS,
        title: `ADS-B · ${name}`,
        label: contact.altM !== null ? `ADS-B ${name} ${Math.round(contact.altM)} m` : `ADS-B ${name}`,
        lines,
        reporters: [reporter],
        vector: moving
          ? [
              [contact.lon, contact.lat],
              offsetLonLat(contact.lat, contact.lon, contact.headingDeg as number, Math.min(3000, (contact.speedMs as number) * 30)),
            ]
          : null,
      });
    }

    for (const point of picture.avoid ?? []) {
      if (!finite(point.lat, point.lon)) continue;
      // Database indices are per aircraft, so two boats' point 0 are not one point.
      keep(`avoid:${picture.droneId}:${point.id}`, {
        kind: 'avoid',
        lat: point.lat,
        lon: point.lon,
        ageS: point.ageS,
        title: 'AVOIDANCE POINT',
        label: 'AVOID',
        lines: [
          `${point.lat.toFixed(5)}, ${point.lon.toFixed(5)}`,
          `${reporter} is steering round this`,
        ],
        reporters: [reporter],
        vector: null,
      });
    }

    const center = picture.center;
    if (center && finite(center.lat, center.lon)) {
      for (const hit of picture.proximity ?? []) {
        if (!finite(hit.bearingDeg, hit.widthDeg, hit.distanceM)) continue;
        const half = hit.widthDeg / 2;
        const step = Math.max(1, hit.widthDeg / 8);
        const coordinates: [number, number][] = [];
        for (let b = hit.bearingDeg - half; b <= hit.bearingDeg + half + 0.01; b += step) {
          coordinates.push(offsetLonLat(center.lat, center.lon, b, hit.distanceM));
        }
        const color =
          hit.distanceM <= PROXIMITY_NEAR_M
            ? SURROUNDINGS_COLOR.near
            : hit.distanceM <= PROXIMITY_MID_M
              ? SURROUNDINGS_COLOR.mid
              : SURROUNDINGS_COLOR.far;
        prox.push({
          type: 'Feature',
          properties: { color, droneId: picture.droneId },
          geometry: { type: 'LineString', coordinates },
        });
      }
    }
  }

  const out: SurroundingsFeatures = { ships: [], traffic: [], avoid: [], vectors: [], prox };
  for (const contact of merged.values()) {
    const detail = [
      ...contact.lines,
      `REPORTED BY ${contact.reporters.join(', ')}`,
      `LAST HEARD ${age(contact.ageS)}`,
      'DISPLAY ONLY',
    ];
    const feature: Feature = {
      type: 'Feature',
      properties: {
        kind: contact.kind,
        title: contact.title,
        label: contact.label,
        color: SURROUNDINGS_COLOR[contact.kind],
        // MapLibre flattens properties to primitives, so the card's lines
        // travel as JSON.
        lines: JSON.stringify(detail),
        // Read by anything that has to answer "is this one of ours?".
        reported: true,
      },
      geometry: { type: 'Point', coordinates: [contact.lon, contact.lat] },
    };
    if (contact.kind === 'ship') out.ships.push(feature);
    else if (contact.kind === 'traffic') out.traffic.push(feature);
    else out.avoid.push(feature);
    if (contact.vector) {
      out.vectors.push({
        type: 'Feature',
        properties: { color: SURROUNDINGS_COLOR[contact.kind] },
        geometry: { type: 'LineString', coordinates: contact.vector },
      });
    }
  }
  return out;
}

export interface SurroundingsSelection {
  x: number;
  y: number;
  title: string;
  lines: string[];
  color: string;
}

interface ClickEvent {
  features?: Array<{ properties: Record<string, unknown> | null } | undefined>;
  point: { x: number; y: number };
}

export function selectionFromProperties(
  properties: Record<string, unknown>,
  point: { x: number; y: number },
): SurroundingsSelection | null {
  if (typeof properties.title !== 'string') return null;
  let lines: string[] = [];
  if (typeof properties.lines === 'string') {
    try {
      const parsed: unknown = JSON.parse(properties.lines);
      if (Array.isArray(parsed)) lines = parsed.filter((l): l is string => typeof l === 'string');
    } catch {
      lines = [];
    }
  }
  const color = typeof properties.color === 'string' ? properties.color : SURROUNDINGS_COLOR.traffic;
  return { x: point.x, y: point.y, title: properties.title, lines, color };
}

const EMPTY = { type: 'FeatureCollection', features: [] as unknown[] };

export interface SurroundingsLayer {
  draw: (pictures: SurroundingsPicture[]) => void;
  setVisible: (on: boolean) => void;
  destroy: () => void;
  stats: () => { redraws: number; contacts: number };
}

export function createSurroundingsLayer(
  map: MapLike,
  options: {
    onSelect?: (selection: SurroundingsSelection | null) => void;
    /** The pointer entered (true) or left (false) something clickable. */
    onPointer?: (over: boolean) => void;
  } = {},
): SurroundingsLayer {
  let redraws = 0;
  let contacts = 0;

  for (const id of Object.values(SOURCES)) {
    if (!map.getSource(id)) map.addSource(id, { type: 'geojson', data: EMPTY });
  }

  const add = (layer: { id: string } & Record<string, unknown>): void => {
    if (!map.getLayer(layer.id)) map.addLayer(layer);
  };
  add({
    id: PROX_LAYER,
    type: 'line',
    source: SOURCES.prox,
    layout: { 'line-cap': 'round' },
    paint: { 'line-color': ['get', 'color'], 'line-width': 4, 'line-opacity': 0.85 },
  });
  add({
    id: VECTOR_LAYER,
    type: 'line',
    source: SOURCES.vectors,
    paint: { 'line-color': ['get', 'color'], 'line-width': 1.5, 'line-opacity': 0.8, 'line-dasharray': [2, 2] },
  });
  add({
    id: AVOID_LAYER,
    type: 'circle',
    source: SOURCES.avoid,
    paint: {
      'circle-radius': 8,
      'circle-color': 'rgba(237,64,66,0.22)',
      'circle-stroke-color': SURROUNDINGS_COLOR.avoid,
      'circle-stroke-width': 2,
    },
  });
  add({
    id: SHIP_LAYER,
    type: 'circle',
    source: SOURCES.ships,
    paint: {
      'circle-radius': 5,
      'circle-color': '#0b3d44',
      'circle-stroke-color': SURROUNDINGS_COLOR.ship,
      'circle-stroke-width': 1.5,
    },
  });
  add({
    id: TRAFFIC_LAYER,
    type: 'circle',
    source: SOURCES.traffic,
    paint: {
      'circle-radius': 5,
      'circle-color': '#3d2a0b',
      'circle-stroke-color': SURROUNDINGS_COLOR.traffic,
      'circle-stroke-width': 1.5,
    },
  });
  // One label layer over ships and traffic. Avoidance points go unlabelled:
  // there can be dozens round one hull, and the ring already says what they are.
  add({
    id: LABEL_LAYER,
    type: 'symbol',
    source: SOURCES.traffic,
    minzoom: 10,
    layout: { 'text-field': ['get', 'label'], 'text-size': 9.5, 'text-offset': [0, 1.4], 'text-letter-spacing': 0.06 },
    paint: { 'text-color': ['get', 'color'], 'text-halo-color': 'rgba(0,0,0,0.85)', 'text-halo-width': 1.2 },
  });
  add({
    id: `${LABEL_LAYER}-ships`,
    type: 'symbol',
    source: SOURCES.ships,
    // A busy harbour's names would cover the water at anything wider.
    minzoom: 13,
    layout: { 'text-field': ['get', 'label'], 'text-size': 9.5, 'text-offset': [0, 1.4], 'text-letter-spacing': 0.06 },
    paint: { 'text-color': ['get', 'color'], 'text-halo-color': 'rgba(0,0,0,0.85)', 'text-halo-width': 1.2 },
  });

  const layerIds = [...ALL_LAYERS, `${LABEL_LAYER}-ships`];

  const onClick = (event: ClickEvent): void => {
    const properties = event.features?.[0]?.properties;
    if (!properties) return;
    options.onSelect?.(selectionFromProperties(properties, event.point));
  };
  const onEnter = (): void => options.onPointer?.(true);
  const onLeave = (): void => options.onPointer?.(false);
  for (const id of CLICKABLE) {
    map.on('click', id, onClick);
    map.on('mouseenter', id, onEnter);
    map.on('mouseleave', id, onLeave);
  }

  return {
    draw: (pictures) => {
      const callsigns = useDroneStore.getState().drones;
      const features = surroundingsFeatures(pictures, (id) => callsigns.get(id)?.callsign ?? id);
      const collection = (list: Feature[]) => ({ type: 'FeatureCollection', features: list });
      geoJsonSource(map, SOURCES.ships)?.setData(collection(features.ships));
      geoJsonSource(map, SOURCES.traffic)?.setData(collection(features.traffic));
      geoJsonSource(map, SOURCES.avoid)?.setData(collection(features.avoid));
      geoJsonSource(map, SOURCES.vectors)?.setData(collection(features.vectors));
      geoJsonSource(map, SOURCES.prox)?.setData(collection(features.prox));
      contacts = features.ships.length + features.traffic.length + features.avoid.length;
      redraws += 1;
    },
    setVisible: (on) => {
      for (const id of layerIds) {
        if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
      }
      if (!on) options.onSelect?.(null);
    },
    destroy: () => {
      for (const id of CLICKABLE) {
        map.off('click', id, onClick);
        map.off('mouseenter', id, onEnter);
        map.off('mouseleave', id, onLeave);
      }
      options.onSelect?.(null);
      for (const id of [...layerIds].reverse()) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      for (const id of Object.values(SOURCES)) {
        if (map.getSource(id)) map.removeSource(id);
      }
    },
    stats: () => ({ redraws, contacts }),
  };
}

/* The clicked contact, published outside React so the card can be mounted
 * where the screen puts its overlays, as the external-track label is. */
const selectionListeners = new Set<(selection: SurroundingsSelection | null) => void>();

function publishSelection(selection: SurroundingsSelection | null): void {
  for (const listener of selectionListeners) listener(selection);
}

/**
 * Mount the layer and keep it fed.
 *
 * The poll lives with the layer, as the external picture's does, so the
 * Command screen opened directly still draws it.
 */
export function useSurroundingsLayer(): void {
  const controller = useMapController();
  const layerRef = useRef<SurroundingsLayer | null>(null);
  const latest = useRef<SurroundingsPicture[]>([]);
  const visible = useMapStore((state) => state.layers.surroundings);

  useEffect(
    () =>
      controller.ready((map) => {
        const layer = createSurroundingsLayer(map, {
          onSelect: publishSelection,
          onPointer: (over) => {
            const raw = controller.raw();
            if (raw) raw.getCanvas().style.cursor = over ? 'pointer' : '';
          },
        });
        layer.draw(latest.current);
        layer.setVisible(useMapStore.getState().layers.surroundings);
        layerRef.current = layer;
        return () => {
          layerRef.current = null;
          layer.destroy();
        };
      }),
    [controller],
  );

  useEffect(() => {
    layerRef.current?.setVisible(visible);
  }, [visible]);

  useEffect(() => {
    let alive = true;
    const load = async (): Promise<void> => {
      if (document.hidden || !useMapStore.getState().layers.surroundings) return;
      const result = await listSurroundings();
      // A failed poll keeps the last picture rather than blanking the map: the
      // next one is two seconds away, and contacts expire on the server anyway.
      if (!alive || !result.ok) return;
      latest.current = result.data.surroundings;
      try {
        layerRef.current?.draw(latest.current);
      } catch (error) {
        // An overlay must never take the page down; the predecessor's did,
        // with "Style is not done loading" during a reload.
        console.warn('surroundings layer update skipped', error);
      }
    };
    void load();
    const timer = setInterval(() => void load(), SURROUNDINGS_POLL_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);
}

const CARD_WIDTH = 260;

/** The details of whatever reported contact was clicked last. */
export function SurroundingsDetailCard(): ReactNode {
  const [selection, setSelection] = useState<SurroundingsSelection | null>(null);

  useEffect(() => {
    selectionListeners.add(setSelection);
    return () => {
      selectionListeners.delete(setSelection);
    };
  }, []);

  if (!selection) return null;

  const viewportWidth = typeof window === 'undefined' ? 1280 : window.innerWidth;
  const left = Math.min(selection.x + 14, viewportWidth - CARD_WIDTH - 12);

  return (
    <div
      role="dialog"
      aria-label={selection.title}
      className="glass pointer-events-auto absolute z-40 rounded-[12px] px-3 py-2"
      style={{ left, top: selection.y + 14, width: CARD_WIDTH }}
    >
      <div className="flex items-start gap-2">
        <span
          className="min-w-0 flex-1 font-mono text-[11px] font-semibold tracking-wide"
          style={{ color: selection.color }}
        >
          {selection.title}
        </span>
        <button
          type="button"
          aria-label="Close details"
          onClick={() => setSelection(null)}
          className="text-[color:var(--muted-foreground)] hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
      </div>
      {selection.lines.map((line) => (
        <span key={line} className="mt-0.5 block font-mono text-[9.5px] text-white/55">
          {line}
        </span>
      ))}
    </div>
  );
}
